import { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { GiHamburgerMenu, GiMedicines, GiMeepleGroup } from "react-icons/gi";
import { CgProfile } from "react-icons/cg";
import { FaUserMd, FaHospitalUser, FaHandshake, FaFileInvoice, FaPercentage } from "react-icons/fa";
import { MdDashboard, MdAdminPanelSettings, MdReport, MdAcUnit } from "react-icons/md";
import { BsCalendarPlus } from "react-icons/bs";
import { BiLogOut } from "react-icons/bi";
import { Modal, Tooltip } from "antd";
import SyncComponent from "../../../../Components/SyncComponent";
import { useNavigate } from 'react-router';

const Sidebar = ({ onCollapse }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    data: { user },
  } = useSelector((state) => state.auth);

  function toggle() {
    setIsOpen(!isOpen);
    if (onCollapse) {
      onCollapse(!isOpen);
    }
  }

  const handleLogout = () => {
    const pathLastSegment = window.location.pathname.split("/").pop();
    if (pathLastSegment === "super-admin") {
      localStorage.removeItem("superAdminAuth");
      if (user?.userType === "admin") {
        navigate('/admin-dashboard', { replace: true });
        return;
      } else {
        navigate('/', { replace: true });
        return;
      }
    } else {
      dispatch({ type: "AUTH_LOGOUT" });
      navigate('/', { replace: true });
    }
  };

  const isHashBuildForOffline = (process.env.REACT_APP_BUILD_ROUTER === 'HASH');

  const renderNavItem = (to, icon, label, onClick = null, isLogout = false) => {
    const content = (
      <div className="w-full">
        {onClick ? (
          <button
            className={`flex items-center px-4 mx-2 rounded-lg transition-colors duration-200 group w-full text-left ${isLogout
                ? "hover:bg-red-600/20 text-red-400"
                : "hover:bg-slate-700"
              } ${isOpen ? "py-2" : "py-1"}`}
            onClick={onClick}
          >
            <div className="flex items-center justify-center w-8 h-8">
              {icon}
            </div>
            <span
              className={`ml-3 font-medium transition-all duration-300 ${isOpen
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-4"
                }`}
            >
              {label}
            </span>
          </button>
        ) : (
          <Link
            className={`flex items-center px-4 mx-2 rounded-lg hover:bg-slate-700 transition-colors duration-200 group ${isOpen ? "py-2" : "py-1"
              }`}
            to={to}
          >
            <div className="flex items-center justify-center w-8 h-8">
              {icon}
            </div>
            <span
              className={`ml-3 font-medium transition-all duration-300 ${isOpen
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-4"
                }`}
            >
              {label}
            </span>
          </Link>
        )}
      </div>
    );

    return (
      <Tooltip
        title={!isOpen ? label : ""}
        placement="right"
        key={label}
      >
        {content}
      </Tooltip>
    );
  };

  return (
    <div className="relative">
      {isHashBuildForOffline && (
        <div className="fixed top-1 right-1 z-50">
          <SyncComponent />
        </div>
      )}

      <div
        className={`
          fixed left-0 top-0 h-screen overflow-y-auto bg-gradient-to-b from-slate-800 to-slate-900 
          text-white transition-all duration-300 ease-in-out z-50 shadow-2xl overflow-x-hidden
          ${isOpen ? 'w-72' : 'w-24'}
        `}
      >
        <div className="flex items-center p-5 border-b border-slate-700">
          <Tooltip title={!isOpen ? "Expand Sidebar" : "Collapse Sidebar"} placement="right">
            <button
              onClick={toggle}
              className="p-2 rounded-full hover:bg-slate-700 transition-colors duration-200"
            >
              <GiHamburgerMenu className="text-xl" />
            </button>
          </Tooltip>
          <img
            src={`${process.env.PUBLIC_URL || '.'}/mediplus.png`}
            alt="MediPlus"
            className={`
              h-14 md:h-16 object-contain
              ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}
              transition-opacity duration-200
            `}
          />
        </div>

        <div className="flex flex-col h-full max-h-[80vh]">
          <nav className="flex-1 py-4 flex flex-col items-between justify-center gap-1">
            {user?.userType === "manager" &&
              renderNavItem("/manager-profile", <CgProfile className="text-xl text-blue-400 group-hover:text-blue-300" />, "Profile")}

            {user?.userType === "manager" &&
              renderNavItem("/dashboard", <MdDashboard className="text-xl text-green-400 group-hover:text-green-300" />, "Dashboard")}

            {user?.userType === "admin" &&
              renderNavItem("/admin-dashboard", <MdDashboard className="text-xl text-purple-400 group-hover:text-purple-300" />, "Admin Dashboard")}

            {user?.userType === "manager" &&
              renderNavItem("/bookappointment", <BsCalendarPlus className="text-xl text-indigo-400 group-hover:text-indigo-300" />, "Book Appointments")}

            {(user?.userType === "manager" || user?.userType === "admin") &&
              renderNavItem("/testorders", <GiMedicines className="text-xl text-emerald-400 group-hover:text-emerald-300" />, "Test Orders")}

            {user?.userType === "manager" &&
              renderNavItem("/adddoctor", <FaUserMd className="text-xl text-cyan-400 group-hover:text-cyan-300" />, "Add Doctor")}

            {user?.userType === "manager" &&
              renderNavItem("/report", <MdReport className="text-xl text-cyan-400 group-hover:text-cyan-300" />, "Report")}

            {user?.userType === "admin" &&
              renderNavItem("/addManager", <FaHospitalUser className="text-xl text-pink-400 group-hover:text-pink-300" />, "Add Manager")}

            {user?.userType === "admin" &&
              renderNavItem("/admin", <MdAdminPanelSettings className="text-xl text-orange-400 group-hover:text-orange-300" />, "Add Admin")}

            {user?.userType === "admin" &&
              renderNavItem("/admin-activities", <MdAcUnit className="text-xl text-teal-400 group-hover:text-teal-300" />, "Activities")}

            {user?.userType === "admin" &&
              renderNavItem("/super-admin", <MdAdminPanelSettings className="text-xl text-rose-400 group-hover:text-rose-300" />, "Super Admin")}

            {(user?.userType === "manager" || user?.userType === "admin") &&
              renderNavItem("/test-management", <GiMedicines className="text-xl text-purple-400 group-hover:text-purple-300" />, "Test Management")}

            {(user?.userType === "manager" || user?.userType === "admin") &&
              renderNavItem("/test-group-management", <GiMeepleGroup className="text-xl text-green-400 group-hover:text-green-600" />, "Test Groups")}

            {user?.userType === "manager" &&
              renderNavItem("/addagent", <FaHandshake className="text-xl text-yellow-400 group-hover:text-yellow-300" />, "Add Agent")}

            {user?.userType === "admin" &&
              renderNavItem("/commission-percentage", <FaPercentage className="text-xl text-amber-500 group-hover:text-amber-400" />, "Commission Percentage")}

            {user?.userType === "admin" &&
              renderNavItem("/commission", <FaFileInvoice className="text-xl text-blue-500 group-hover:text-blue-400" />, "Collection Commission")}

            <div className="flex-1"></div>

            <div className="border-t border-slate-700 pt-4 mt-auto">
              {renderNavItem(
                null,
                <BiLogOut className="text-xl group-hover:text-red-300" />,
                "Logout",
                () => setLogoutModalVisible(true),
                true
              )}

            </div>
          </nav>
        </div>
      </div>

      <Modal
        title="Confirm Logout"
        visible={logoutModalVisible}
        onCancel={() => setLogoutModalVisible(false)}
        footer={[
          <button
            key="cancel"
            onClick={() => setLogoutModalVisible(false)}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
          >
            Cancel
          </button>,
          <button
            key="logout"
            onClick={handleLogout}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 ml-2"
          >
            Logout
          </button>,
        ]}
      >
        <p>Are you sure you want to logout?</p>
      </Modal>
    </div>
  );
};

export default Sidebar;